import { Signal } from '@angular/core';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import { Observable, catchError, of, switchMap, take } from 'rxjs';
import {
  GridRowsPage,
  GridGroupSummaryItem,
  GridGroupTreeNode,
  GridGroupTreePage
} from '../contracts/public-api';
import type {
  GridBackendAttributeFilter,
  GridBackendAttributeFilterType
} from '../d-data-grid/column-filter.types';
import {
  applyElementsTotalCount,
  mergePrimaryCountsIntoPagination,
  type PrimaryCountsMergeResult
} from './grid-orchestration-primary-counts';
import { isStaleQueryGeneration } from './grid-orchestration-query-generation';
import {
  COLLAPSE_CARDINALITY_THRESHOLD,
  GROUP_PATH_DELIMITER,
  MAX_DISCOVERY_QUERIES,
  MAX_GROUP_TREE_PATHS,
  NULL_BACKEND_SENTINEL,
  NULL_DISPLAY_KEY,
  isNullSentinel
} from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import type { GroupPaginationState, GroupingCapWarning } from './grid-orchestration.types';
import {
  MAX_REQUEST_CONCURRENCY,
  buildSubGroupCountsFromPaths,
  computeMaxDistinctChildrenPerParent,
  forkJoinWithConcurrency,
  normalizeGroupSummaryKey
} from './grid-orchestration.utils';

/** Scoped parent entry for a sub-group summary query. */
export interface ScopedParentEntry {
  readonly pathPrefix: string;
  readonly where: Record<string, unknown>;
  readonly elementTypes: Array<string>;
  readonly attributeFilter: Array<GridBackendAttributeFilter>;
}

/** Host callbacks/state accessors used by group discovery helpers. */
export interface GroupDiscoveryHost {
  readonly elementsProvider: GridOrchestrationDataAccess;
  getQueryGeneration: () => number;
  mergeAttributeFilters: (
    extra?: Array<GridBackendAttributeFilter>
  ) => Array<GridBackendAttributeFilter> | undefined;
  remappedFields: () => ReadonlySet<string>;
  resolveColumnType: (field: string) => GridBackendAttributeFilterType | undefined;
  buildNullOperatorEntry: (name: string, operator: 'IS_NULL' | 'IS_NOT_NULL') => GridBackendAttributeFilter;
  getPrimaryGroupField: () => string | null;
  getDiscriminatorField: () => string;
  getGroupPagination: () => Map<string, GroupPaginationState> | null;
  setGroupPagination: (pagination: Map<string, GroupPaginationState>) => void;
  getSubGroupCounts: () => Record<string, number>;
  setSubGroupCounts: (counts: Record<string, number>) => void;
  addHighCardinalityField: (field: string) => void;
  getElements: () => GridRowsPage | null;
  setElements: (response: GridRowsPage) => void;
  reportGroupingCapExceeded: (warning: GroupingCapWarning) => void;
}

/**
 * Walks every page of the group summary tree, following `pageInfo.endCursor`.
 *
 * The backend paginates leaf paths, so reading only the first page silently
 * drops groups: those buckets get no server count and fall back to their loaded
 * row count, which caps them at one page. Stops early and reports the result as
 * truncated once `maxPaths` is reached so the caller can fall back.
 *
 * @param host - Discovery host providing the data access layer.
 * @param groupFields - Ordered group chain.
 * @param selectedElementTypes - Active element type scopes.
 * @param baseWhere - Filter applied to every page.
 * @param attributeFilter - Merged attribute filters, when any apply.
 * @param maxPaths - Upper bound on accumulated leaf paths.
 * @returns The accumulated tree across all fetched pages.
 */
export function fetchAllGroupSummaryTreePages(
  host: GroupDiscoveryHost,
  groupFields: Array<string>,
  selectedElementTypes: Signal<Array<string>>,
  baseWhere: Record<string, unknown>,
  attributeFilter: Array<GridBackendAttributeFilter> | undefined,
  maxPaths: number = MAX_GROUP_TREE_PATHS
): Observable<GridGroupTreePage> {
  const fetchPage = (
    after: string | undefined,
    accumulated: ReadonlyArray<GridGroupTreeNode>
  ): Observable<GridGroupTreePage> =>
    host.elementsProvider
      .getGroupSummaryTree(groupFields, selectedElementTypes(), {
        where: baseWhere,
        ...(attributeFilter !== undefined ? { attributeFilter } : {}),
        ...(after !== undefined ? { after } : {})
      })
      .pipe(
        take(1),
        switchMap((page: GridGroupTreePage): Observable<GridGroupTreePage> => {
          const nodes: ReadonlyArray<GridGroupTreeNode> = accumulated.concat(page.nodes);
          const endCursor: string | null = page.pageInfo?.endCursor ?? null;
          const hasNextPage: boolean = page.pageInfo?.hasNextPage === true && endCursor !== null;

          if (page.truncated || !hasNextPage) {
            return of({ ...page, nodes });
          }
          if (nodes.length >= maxPaths) {
            return of({ ...page, nodes, truncated: true });
          }
          return fetchPage(endCursor ?? undefined, nodes);
        })
      );

  return fetchPage(undefined, new Array<GridGroupTreeNode>());
}

/**
 * Discovers sub-group counts via a multi-field tree query when the backend
 * returns a complete (non-truncated) result. Falls back to per-parent discovery
 * when the tree is truncated or the query fails.
 */
export function discoverSubGroupsViaTree(
  host: GroupDiscoveryHost,
  selectedElementTypes: Signal<Array<string>>,
  groupFields: Array<string>,
  baseWhere: Record<string, unknown>,
  initialResponse: GridRowsPage,
  discoverSubGroupsFallback: (
    selectedElementTypes: Signal<Array<string>>,
    remainingGroupFields: Array<string>,
    baseWhere: Record<string, unknown>,
    initialResponse: GridRowsPage,
    ancestorConstraints?: Array<{ field: string; values: Array<string> }>
  ) => Observable<GridRowsPage>,
  maxPaths: number = MAX_GROUP_TREE_PATHS
): Observable<GridRowsPage> {
  if (groupFields.length < 2) {
    return of(initialResponse);
  }

  const attributeFilter: Array<GridBackendAttributeFilter> | undefined = host.mergeAttributeFilters();
  const queryGeneration: number = host.getQueryGeneration();
  return fetchAllGroupSummaryTreePages(
    host,
    groupFields,
    selectedElementTypes,
    baseWhere,
    attributeFilter,
    maxPaths
  ).pipe(
    switchMap((tree: GridGroupTreePage): Observable<GridRowsPage> => {
      if (isStaleQueryGeneration(host, queryGeneration)) {
        logGridOrchestration(
          GridOrchestrationLogLevel.Warn,
          '[GridDiag] tree result DISCARDED as stale; sub-group counts stay empty.'
        );
        return of(initialResponse);
      }
      if (tree.truncated) {
        logGridOrchestration(
          GridOrchestrationLogLevel.Warn,
          '[GridOrchestration] elementGroupSummaryTree truncated; falling back to per-parent discovery.'
        );
        const skippedCount: number = Math.max(0, tree.totalPathCount - tree.nodes.length);
        host.reportGroupingCapExceeded({
          kind: 'subGroupDiscovery',
          skippedCount,
          cap: tree.nodes.length,
          skippedCountUnknown: skippedCount === 0
        });
        return discoverSubGroupsFallback(
          selectedElementTypes,
          groupFields.slice(1),
          baseWhere,
          initialResponse
        );
      }

      applyCompleteGroupSummaryTree(host, groupFields, tree.nodes);

      return of(initialResponse);
    }),
    catchError((err: Error): Observable<GridRowsPage> => {
      if (isStaleQueryGeneration(host, queryGeneration)) {
        return of(initialResponse);
      }
      logGridOrchestration(
        GridOrchestrationLogLevel.Error,
        '[GridOrchestration] elementGroupSummaryTree failed; falling back to per-parent discovery:',
        err
      );
      return discoverSubGroupsFallback(
        selectedElementTypes,
        groupFields.slice(1),
        baseWhere,
        initialResponse
      );
    })
  );
}

/**
 * Applies a complete (non-truncated) group summary tree to orchestration state.
 */
export function applyCompleteGroupSummaryTree(
  host: GroupDiscoveryHost,
  groupFields: Array<string>,
  nodes: ReadonlyArray<{ path: string; count: number }>
): void {
  const treeCounts: Record<string, number> = buildSubGroupCountsFromPaths(
    nodes,
    NULL_BACKEND_SENTINEL,
    NULL_DISPLAY_KEY
  );
  host.setSubGroupCounts({ ...host.getSubGroupCounts(), ...treeCounts });

  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (pagination !== null) {
    const mergeResult: PrimaryCountsMergeResult = mergePrimaryCountsIntoPagination(pagination, nodes);
    host.setGroupPagination(mergeResult.pagination);
    host.setElements(applyElementsTotalCount(host.getElements(), mergeResult.elementsTotalCount));
  }

  const subFields: Array<string> = groupFields.slice(1);
  const maxDistinctPerParent: number = computeMaxDistinctChildrenPerParent(treeCounts);
  if (maxDistinctPerParent > COLLAPSE_CARDINALITY_THRESHOLD && subFields.length > 0) {
    host.addHighCardinalityField(subFields[0]);
  }
}

/**
 * Recursively discovers sub-group values at every depth of the grouping
 * hierarchy via server-side summary queries.
 */
export function discoverSubGroups(
  host: GroupDiscoveryHost,
  selectedElementTypes: Signal<Array<string>>,
  remainingGroupFields: Array<string>,
  baseWhere: Record<string, unknown>,
  initialResponse: GridRowsPage,
  ancestorConstraints: Array<{ field: string; values: Array<string> }> = []
): Observable<GridRowsPage> {
  if (remainingGroupFields.length === 0) {
    return of(initialResponse);
  }

  const primaryField: string | null = host.getPrimaryGroupField();
  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (primaryField === null || pagination === null) {
    return of(initialResponse);
  }

  const currentField: string = remainingGroupFields[0];
  const deeperFields: Array<string> = remainingGroupFields.slice(1);

  const parentEntries: Array<ScopedParentEntry> = buildScopedParentEntries(
    host,
    selectedElementTypes,
    primaryField,
    pagination,
    ancestorConstraints,
    baseWhere
  );
  const queryGeneration: number = host.getQueryGeneration();

  if (parentEntries.length > MAX_DISCOVERY_QUERIES) {
    const skippedCount: number = parentEntries.length - MAX_DISCOVERY_QUERIES;
    logGridOrchestration(
      GridOrchestrationLogLevel.Warn,
      `[GridOrchestration] Sub-group discovery capped at ${MAX_DISCOVERY_QUERIES} parents; ` +
        `${skippedCount} parent bucket(s) skipped.`
    );
    host.reportGroupingCapExceeded({
      kind: 'subGroupDiscovery',
      skippedCount,
      cap: MAX_DISCOVERY_QUERIES
    });
  }

  const cappedParentEntries: Array<ScopedParentEntry> =
    parentEntries.length > MAX_DISCOVERY_QUERIES
      ? parentEntries.slice(0, MAX_DISCOVERY_QUERIES)
      : parentEntries;

  const summaryQueryEntries: Array<[string, Observable<Array<GridGroupSummaryItem>>]> = [];
  for (const entry of cappedParentEntries) {
    summaryQueryEntries.push([
      entry.pathPrefix,
      host.elementsProvider
        .getGroupSummary(
          currentField,
          entry.elementTypes,
          entry.where,
          host.mergeAttributeFilters(entry.attributeFilter)
        )
        .pipe(
          take(1),
          catchError((err: Error): Observable<Array<GridGroupSummaryItem>> => {
            logGridOrchestration(
              GridOrchestrationLogLevel.Error,
              `Sub-group summary failed for path '${entry.pathPrefix}', field '${currentField}':`,
              err
            );
            return of(new Array<GridGroupSummaryItem>());
          })
        )
    ]);
  }

  return forkJoinWithConcurrency(summaryQueryEntries, MAX_REQUEST_CONCURRENCY).pipe(
    switchMap((summaries: Record<string, Array<GridGroupSummaryItem>>): Observable<GridRowsPage> => {
      if (isStaleQueryGeneration(host, queryGeneration)) {
        return of(initialResponse);
      }
      const existingCounts: Record<string, number> = { ...host.getSubGroupCounts() };
      let maxDistinctPerParent: number = 0;
      for (const [pathPrefix, groups] of Object.entries(summaries)) {
        maxDistinctPerParent = Math.max(maxDistinctPerParent, groups.length);
        for (const group of groups) {
          const normalizedValue: string = normalizeGroupSummaryKey(
            group.value,
            NULL_BACKEND_SENTINEL,
            NULL_DISPLAY_KEY
          );
          existingCounts[`${pathPrefix}:::${normalizedValue}`] = group.count;
        }
      }
      host.setSubGroupCounts(existingCounts);

      if (maxDistinctPerParent > COLLAPSE_CARDINALITY_THRESHOLD) {
        host.addHighCardinalityField(currentField);
      }

      if (deeperFields.length > 0) {
        const currentLevelValues: Set<string> = new Set<string>();
        for (const groups of Object.values(summaries)) {
          for (const group of groups) {
            currentLevelValues.add(group.value);
          }
        }
        const nextAncestors: Array<{ field: string; values: Array<string> }> = [
          ...ancestorConstraints,
          { field: currentField, values: Array.from(currentLevelValues) }
        ];
        return discoverSubGroups(
          host,
          selectedElementTypes,
          deeperFields,
          baseWhere,
          initialResponse,
          nextAncestors
        );
      }

      return of(initialResponse);
    })
  );
}

/** Builds scoped parent entries for sub-group discovery queries. */
export function buildScopedParentEntries(
  host: GroupDiscoveryHost,
  selectedElementTypes: Signal<Array<string>>,
  primaryField: string,
  pagination: Map<string, GroupPaginationState>,
  ancestorConstraints: Array<{ field: string; values: Array<string> }>,
  baseWhere: Record<string, unknown>
): Array<ScopedParentEntry> {
  const aliases: ReadonlySet<string> = host.remappedFields();

  if (ancestorConstraints.length === 0) {
    const result: Array<ScopedParentEntry> = [];
    for (const key of pagination.keys()) {
      if (key.includes(GROUP_PATH_DELIMITER)) {
        continue;
      }
      const scopedWhere: Record<string, unknown> = { ...baseWhere };
      const attributeFilter: Array<GridBackendAttributeFilter> = [];
      let elementTypes: Array<string> = selectedElementTypes();
      const discriminatorField: string = host.getDiscriminatorField();
      if (discriminatorField.length > 0 && primaryField === discriminatorField) {
        elementTypes = [key];
        scopedWhere[discriminatorField] = { in: [key] };
      } else if (aliases.has(primaryField)) {
        attributeFilter.push(buildAliasAncestorFilter(host, primaryField, key));
      } else {
        scopedWhere[primaryField] = key === NULL_DISPLAY_KEY ? { eq: null } : { eq: key };
      }
      result.push({ pathPrefix: key, where: scopedWhere, elementTypes, attributeFilter });
    }
    return result;
  }

  type PathEntry = {
    parts: Array<string>;
    where: Record<string, unknown>;
    elementTypes: Array<string>;
    attributeFilter: Array<GridBackendAttributeFilter>;
  };
  let entries: Array<PathEntry> = [
    { parts: [], where: { ...baseWhere }, elementTypes: selectedElementTypes(), attributeFilter: [] }
  ];

  const primaryKeys: Array<string> = Array.from(pagination.keys()).filter(
    (key: string): boolean => !key.includes(GROUP_PATH_DELIMITER)
  );
  const allLayers: Array<{ field: string; values: Array<string> }> = [
    { field: primaryField, values: primaryKeys },
    ...ancestorConstraints
  ];

  for (const layer of allLayers) {
    const next: Array<PathEntry> = [];
    for (const entry of entries) {
      for (const val of layer.values) {
        const nullSentinel: boolean = isNullSentinel(val);
        const normalizedVal: string = nullSentinel ? NULL_DISPLAY_KEY : val;
        const newWhere: Record<string, unknown> = { ...entry.where };
        let newElementTypes: Array<string> = [...entry.elementTypes];
        const newAttributeFilter: Array<GridBackendAttributeFilter> = [...entry.attributeFilter];
        const discriminatorField: string = host.getDiscriminatorField();
        if (discriminatorField.length > 0 && layer.field === discriminatorField) {
          newElementTypes = [val];
          newWhere[discriminatorField] = { in: [val] };
        } else if (aliases.has(layer.field)) {
          newAttributeFilter.push(buildAliasAncestorFilter(host, layer.field, val));
        } else {
          newWhere[layer.field] = nullSentinel ? { eq: null } : { eq: val };
        }
        next.push({
          parts: [...entry.parts, normalizedVal],
          where: newWhere,
          elementTypes: newElementTypes,
          attributeFilter: newAttributeFilter
        });
      }
    }
    entries = next;
  }

  return entries.map((entry: PathEntry): ScopedParentEntry => ({
    pathPrefix: entry.parts.join(':::'),
    where: entry.where,
    elementTypes: entry.elementTypes,
    attributeFilter: entry.attributeFilter
  }));
}

function buildAliasAncestorFilter(
  host: GroupDiscoveryHost,
  field: string,
  value: string
): GridBackendAttributeFilter {
  if (isNullSentinel(value)) {
    return host.buildNullOperatorEntry(field, 'IS_NULL');
  }
  const type: GridBackendAttributeFilterType = host.resolveColumnType(field) ?? 'TEXT';
  return { name: field, operator: 'EQ', value: value, type: type };
}
